import React, { useEffect, useRef, useState } from "react";
import { ExtendedNodeData, find, RCCAlert, RCCButton, RCCDropDownItem, RCCGrid, RCCHierarchicalTree, RCCHookToast, RCCIcon, RCCRefHierarchicalTree, RCCServicesApi, RCCTooltip, toggleExpandedForAll } from "react-common-components";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import LoadingContainer from "src/components/LoadingContainer";
import ModalIndicator from "src/components/ModalIndicator";
import { IRefIndicatorModal } from "src/components/ModalIndicator/interface";
import { useTranslation } from "src/context/Translations/context";
import DeleteModalIndicator from "src/pages/Indicators/Indicators/deleteModalIndicator";
import { IRefDeleteIndicatorModal } from "src/pages/Indicators/Indicators/interface";
import { standardErrorMessages } from "src/utils/errors";
import { IHierarchy } from "./interface";
import * as HierarchyStyles from "./styles";

const HierarchyNode: React.FC<ExtendedNodeData> = ({
	node,
	refModalIndicator,
	refDeleteModalIndicator,
	updateSelectedLine,
	callback,
	addIndicator
}) => {
	const {translate} = useTranslation();
	return(
		<HierarchyStyles.HierarchyDivContainer>
			<HierarchyStyles.Title title={node.name}>
				{node.name} {node.integrationCode && node.integrationCode !== null && node.integrationCode.length > 0 && `(${node.integrationCode})` }
			</HierarchyStyles.Title> 
			<HierarchyStyles.StyledDiv>
				{node.indicatorName !== null && (
					<small>
						[{node.indicatorName}]
					</small>
				)}
				<OptionsColumn styleInline="font-weight: 500;" position="leftEnd">
					{node.indicatorId === null && (
						<RCCDropDownItem selfEnclose onClick={() => addIndicator(node.id)}>
							<RCCIcon name="plus"/>
							{translate("Adicionar indicador")}
						</RCCDropDownItem>
					)}
					{node.indicatorId !== null && (
						<>
							<RCCDropDownItem selfEnclose onClick={() => {
								updateSelectedLine(node.id);
								refModalIndicator.current?.actionButtonChange(node.indicatorId, false, callback);
							}} >
								<RCCIcon name="pencil" />
								{translate("Alterar indicador")}
							</RCCDropDownItem>
							<RCCDropDownItem selfEnclose onClick={() => {
								updateSelectedLine(node.id);
								refDeleteModalIndicator.current?.actionButtonDelete({
									iqc001Id: node.indicatorId,
									iqc001Descricao: node.indicatorName
								}, callback);
							}}>
								<RCCIcon name="trash" />
								{translate("Excluir indicador")}
							</RCCDropDownItem>
						</>
					)}
				</OptionsColumn>
			</HierarchyStyles.StyledDiv>
		</HierarchyStyles.HierarchyDivContainer>
	);};


const HierarchyFinancialManagementSubsidiaryAndStatementLinesIndicators: React.FC<IHierarchy> = ({
	ofa001Id,
	ofm002Id
}) => {
	const {translate, currentLanguage} = useTranslation();
	const {addToast} = RCCHookToast();
	const [hierarchy, setHierarchy] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);
	const getNodeKeyFunction = ({node}:any) => node.id;
	const [expanded, setExpanded] = useState(false);
	const selectedLineId = useRef<number | undefined>(undefined);

	const refModalIndicator = useRef<IRefIndicatorModal>(null);
	const refDeleteModalIndicator = useRef<IRefDeleteIndicatorModal>(null);
	const refHierarchicalTree = useRef<RCCRefHierarchicalTree>(null);
	const updateSelectedLine = (id: number) => {
		selectedLineId.current = id;
	};
	const getHierarchy = async () => {      
		try{           
			setLoading(true);
			const {data} = await RCCServicesApi.getRequest({
				method: "IncomeStatement/ListHierarchySubsidiaryAndStatementLinesIndicators",
				urlParameters: {
					ofa001Id,
					ofm002Id
				}
			});
			let hierarchyData = toggleExpandedForAll({treeData: [...data], expanded: true});
			if(!selectedLineId.current)
				updateSelectedLine(data[0].id);
			else
				hierarchyData = find({
					getNodeKey:getNodeKeyFunction, 
					expandAllMatchPaths: true,
					treeData: [...data],
					searchMethod:(searchData:any) => searchData.node.id === selectedLineId.current}
				)?.treeData;

			setHierarchy(hierarchyData);
			setLoading(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os itens"), addToast);
			setLoading(false);
		}
	};

	const expandAll = () => {
		const expandedData = toggleExpandedForAll({treeData:[...hierarchy], expanded: !expanded});
		setHierarchy(expandedData);
		setExpanded(!expanded);
	};

	const addIndicator = async (ofa002Id: number) => {
		try{
			updateSelectedLine(ofa002Id);           
			await RCCServicesApi.postRequest({
				method: "IncomeStatement/InsertIndicator",
				urlParameters: {
					ofa001Id,
					ofm002Id,
					ofa002Id,
					currentLanguage
				}
			});
			getHierarchy();
		}catch(err){
			standardErrorMessages(err, translate("Erro ao adicionar indicador"), addToast);
		}
	};


	useEffect(() => {
		getHierarchy();
	}, []);

	return (
		<>
			<RCCGrid container item direction="column" style={{ display: "flex", width: "100%", flex: 1}}>
				<RCCGrid container item justifyContent="space-between" alignItems="center">
					<div>&nbsp;</div>
					<HierarchyStyles.StyledDiv>
						<RCCTooltip
							color="grey"
							text={translate("Expandir/Comprimir")}
							position="left"
						>
							<RCCButton 
								color="transparent"
								icon={{
									name: expanded ? "compress" : "expand",
									color: "var(--default-color-font)",
									size: 16
								}}
								onClick={expandAll}
							/>
						</RCCTooltip>
						<RCCTooltip
							color="grey"
							text={translate("Pesquisar")}
							position="left"
						>
							<RCCButton 
								color="transparent"
								icon={{
									name: "search",
									color: "var(--default-color-font)",
									size: 16
								}}
								onClick={() => refHierarchicalTree.current?.toggleSearch()}
							/>
						</RCCTooltip>
					</HierarchyStyles.StyledDiv>
				</RCCGrid>
				<LoadingContainer loading={loading} styleInline="width: 100%; >div:last-child{ display: flex; width: 100%; flex: 1; }">
					{hierarchy && hierarchy.length === 0 ? (
						<RCCAlert
							title={translate("Atenção")}
							description={translate("Não foram encontrados dados")}
						/>
					): (
						<RCCHierarchicalTree
							data={hierarchy}
							styleInline="width: 100%; .rst__tree{ height: 100%; padding-bottom: 100px; }"
							ref={refHierarchicalTree}
							isSearchable={{
								searchKeyOfNode: "name",
								placeholderInput: translate("Pesquisar")
							}}
							getNodeKey={getNodeKeyFunction}
							onChange={(treeData) => setHierarchy(treeData)}
							scaffoldBlockPxWidth={24}
							generateNodeProps={(row:any) => ({
								title: <HierarchyNode 
									node={row.node}
									path={row.path} 
									treeIndex={row.treeIndex} 
									lowerSiblingCounts={row.lowerSiblingCounts} 
									isSearchMatch={row.isSearchMatch}
									isSearchFocus={row.isSearchFocus}
									parentNode={row.parentNode}
									refModalIndicator={refModalIndicator}
									refDeleteModalIndicator={refDeleteModalIndicator}
									updateSelectedLine={updateSelectedLine}
									addIndicator={addIndicator}
									callback={getHierarchy}
								/>
							})}
							canDrop={() => false}
							canDrag={() => false}
						/>
					)}
				</LoadingContainer>
			</RCCGrid>

			<ModalIndicator 
				ref={refModalIndicator}
			/>

			<DeleteModalIndicator 
				ref={refDeleteModalIndicator}
			/>
		</>
	);
};

export default HierarchyFinancialManagementSubsidiaryAndStatementLinesIndicators;
